import { Injectable } from '@angular/core';
import { Actions, ofActionDispatched, Select, Store } from '@ngxs/store';
import {
    AddUsersToAudience,
    AddUsersToAudienceFailure,
    AddUsersToAudienceSuccess,
    AssignUserToAudience,
    AssignUserToAudienceFailure,
    AssignUserToAudienceSuccess,
    DeleteAudienceUsers,
    DeleteAudienceUsersFailure,
    DeleteAudienceUsersSuccess,
    FetchAudienceByUserId,
    FetchAudienceByUserIdFailure,
    FetchAudienceByUserIdSuccess,
    MoveAudienceUsers,
    MoveAudienceUsersFailure,
    MoveAudienceUsersSuccess,
} from 'app/library/audience-user/actions';
import { UserAudienceCategory } from 'app/library/audience-user/enums/user-audience-category.enum';
import { IFetchUserAudiencesByCategoryResult } from 'app/library/audience-user/interfaces/fetch-user-audiences-by-category-result.interface';
import { AudienceUserExtended } from 'app/library/audience-user/models/audience-user.extended.model';
import { AudienceUserApiService } from 'app/library/audience-user/services/audience-user.api-service';
import { AudienceState } from 'app/library/audience/models/audience.state';
import { BehaviorSubject, firstValueFrom, Observable, race } from 'rxjs';
import { map, take } from 'rxjs/operators';
import { UnsetAudienceUsers } from '../actions';
import { IManageAudienceUserActionPayload } from '../interfaces/audience-user-action.action-payload.interface';
import { AudienceUsersState } from '../models/audience-users.state';

@Injectable()
export class AudienceUsersService {
    @Select(AudienceUsersState.getEntities)
    audienceUser$: Observable<AudienceUserExtended[]>;

    private _audienceUsersSubject = new BehaviorSubject<AudienceUserExtended[]>(null);
    public audienceUsersSubject$ = this._audienceUsersSubject.asObservable();

    constructor(private _store: Store, private _action$: Actions, private _audienceUserApiService: AudienceUserApiService) {}

    getAudienceUsers(): AudienceUserExtended[] {
        return this._store.selectSnapshot(AudienceUsersState.getEntities) as AudienceUserExtended[];
    }

    async fetchAudiencesByUserId(userId: string, pageSize?: number, pageIndex?: number): Promise<AudienceUserExtended[]> {
        this._store.dispatch(new FetchAudienceByUserId({ userId, pageSize, pageIndex }));

        return race(
            this._action$.pipe(
                ofActionDispatched(FetchAudienceByUserIdSuccess),
                map((action: FetchAudienceByUserIdSuccess) => {
                    const payload = action.payload as AudienceUserExtended[];
                    this._audienceUsersSubject.next(payload);
                    return payload;
                })
            ),
            this._action$.pipe(
                ofActionDispatched(FetchAudienceByUserIdFailure),
                map(() => null)
            )
        )
            .pipe(take(1))
            .toPromise();
    }

    async fetchAudiencesByUserIdForCategory(
        userId: string,
        category: UserAudienceCategory,
        pageSize: number,
        pageIndex: number
    ): Promise<IFetchUserAudiencesByCategoryResult> {
        const response = await firstValueFrom(this._audienceUserApiService.getAudiencesByUserId(userId, pageSize, pageIndex, category));
        const audienceState = this._store.selectSnapshot(AudienceState);

        const audiences = response.data
            .map((query) => {
                const extended = new AudienceUserExtended(query);

                if (!extended.audience && extended.audienceId) {
                    extended.audience = AudienceState.getEntity(audienceState, extended.audienceId);
                }

                return extended;
            })
            .filter((userAudience) => !!userAudience.audience);

        return {
            audiences,
            totalCount: response.pagination.number_of_results,
            pageCount: response.pagination.number_of_pages,
        };
    }

    addUsersToAudience(
        payload: IManageAudienceUserActionPayload
    ): Promise<{
        newlyAssigned: AudienceUserExtended[];
        alreadyInChildren: AudienceUserExtended[];
        alreadyAssigned: AudienceUserExtended[];
        asyncJobId?: string;
    } | null> {
        this._store.dispatch(new AddUsersToAudience(payload));

        return race(
            this._action$.pipe(
                ofActionDispatched(AddUsersToAudienceSuccess),
                map((action: AddUsersToAudienceSuccess) => action.payload)
            ),
            this._action$.pipe(
                ofActionDispatched(AddUsersToAudienceFailure),
                map(() => null)
            )
        )
            .pipe(take(1))
            .toPromise();
    }

    clearAudienceUsersState() {
        this._store.dispatch(new UnsetAudienceUsers());
    }

    assignUserToAudience(
        payload: IManageAudienceUserActionPayload
    ): Promise<{ newlyAssigned: AudienceUserExtended[]; alreadyInChildren: AudienceUserExtended[]; alreadyAssigned: AudienceUserExtended[] }> {
        this._store.dispatch(new AssignUserToAudience(payload));

        return race(
            this._action$.pipe(
                ofActionDispatched(AssignUserToAudienceSuccess),
                map((action: AssignUserToAudienceSuccess) => {
                    return action.payload;
                })
            ),
            this._action$.pipe(
                ofActionDispatched(AssignUserToAudienceFailure),
                map(() => null)
            )
        )
            .pipe(take(1))
            .toPromise();
    }

    moveAudienceUsers(
        source: string,
        target: string,
        selectedUserIds: { id: string }[]
    ): Promise<{
        newlyAssigned: AudienceUserExtended[];
        alreadyInChildren: AudienceUserExtended[];
        alreadyAssigned: AudienceUserExtended[];
    }> {
        this._store.dispatch(new MoveAudienceUsers({ source, target, selectedUserIds }));

        return firstValueFrom(
            race(
                this._action$.pipe(
                    ofActionDispatched(MoveAudienceUsersSuccess),
                    map((action: MoveAudienceUsersSuccess) => action.payload)
                ),
                this._action$.pipe(
                    ofActionDispatched(MoveAudienceUsersFailure),
                    map((action: MoveAudienceUsersFailure) => {
                        throw action;
                    })
                )
            )
        );
    }

    moveRootAudienceUsers(payload: IManageAudienceUserActionPayload): Promise<{
        newlyAssigned: AudienceUserExtended[];
        alreadyInChildren: AudienceUserExtended[];
        alreadyAssigned: AudienceUserExtended[];
    }> {
        this._store.dispatch(new AddUsersToAudience(payload));

        const success$ = this._action$.pipe(
            ofActionDispatched(AddUsersToAudienceSuccess),
            map((action: AddUsersToAudienceSuccess) => action.payload)
        );

        const failure$ = this._action$.pipe(
            ofActionDispatched(AddUsersToAudienceFailure),
            map((action: AddUsersToAudienceFailure) => {
                throw action; // reject promise on failure
            })
        );

        // firstValueFrom replaces .toPromise() safely
        return firstValueFrom(race(success$, failure$));
    }

    deleteAudienceUsers(payload: { audienceId: string; userIds: string[] }): Promise<boolean> {
        this._store.dispatch(new DeleteAudienceUsers(payload));

        return race(
            this._action$.pipe(
                ofActionDispatched(DeleteAudienceUsersSuccess),
                map((action: DeleteAudienceUsersSuccess) => true)
            ),
            this._action$.pipe(
                ofActionDispatched(DeleteAudienceUsersFailure),
                map((action: DeleteAudienceUsersFailure) => false)
            )
        )
            .pipe(take(1))
            .toPromise();
    }

    /* getUserSectionStatById(id: string): UserSectionStats {
        return UserSectionStatsState.getEntity(this._store.selectSnapshot(UserSectionStatsState), id);
    } */
}
